import { useState, useEffect, useRef } from 'react';
import { Mail, Bell, Link2, CheckCircle, AlertCircle, Loader2, Clock, RefreshCw, Plus, Trash2, Phone, Save, Zap } from 'lucide-react';
import { getEmailSettings, saveEmailSettings, verifyEmail, getVerifyStatus, sendTestEmail, addRecipient, removeRecipient, listRecipients } from '../api/email';

const TABS = [
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'integrations', label: 'Integrations', icon: Link2 },
  { key: 'automation', label: 'Automation', icon: Zap },
];

const MODES = [
  { value: 'auto', label: 'Auto', color: 'bg-green-100 text-green-700 border-green-300' },
  { value: 'approve', label: 'Approve', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  { value: 'notify', label: 'Notify Only', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'off', label: 'Off', color: 'bg-slate-100 text-slate-500 border-slate-300' },
];

const SCHEDULES = [
  { value: 'none', label: 'No schedule' },
  { value: 'hourly', label: 'Every hour' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'custom', label: 'Custom interval' },
];

const AUTOMATION_SECTIONS = [
  {
    key: 'replies',
    title: 'Reply Handling',
    description: 'How to process incoming prospect replies',
    actions: [
      { key: 'replyClassify', label: 'Classify replies', desc: 'AI categorizes reply intent (interested, objection, unsubscribe, etc.)', allowSchedule: false },
      { key: 'replyDraft', label: 'Generate reply drafts', desc: 'AI drafts a response based on context and voice profile', allowSchedule: false },
      { key: 'replySendInterested', label: 'Send reply — Interested leads', desc: 'Auto-send the AI draft to interested / soft-interest prospects', allowSchedule: false },
      { key: 'replySendObjection', label: 'Send reply — Objections', desc: 'Auto-send objection handling response', allowSchedule: false },
      { key: 'replyUnsubscribe', label: 'Process unsubscribe', desc: 'Auto-remove prospect from all sequences and mark do-not-contact', allowSchedule: false },
      { key: 'replyOooPause', label: 'Pause on OOO', desc: 'Auto-pause sequence when out-of-office reply detected, resume after return date', allowSchedule: false },
    ],
  },
  {
    key: 'campaigns',
    title: 'Campaign Execution',
    description: 'Email sequence generation and sending',
    actions: [
      { key: 'campaignGenerate', label: 'Generate email sequences', desc: 'AI creates personalized email sequences for each prospect', allowSchedule: false },
      { key: 'campaignSend', label: 'Send campaign emails', desc: 'Auto-send scheduled emails in active campaigns', allowSchedule: true, defaultSchedule: 'daily' },
      { key: 'campaignFollowup', label: 'Follow-up scheduling', desc: 'Auto-schedule next steps based on sequence timing (Day 0, 3, 7...)', allowSchedule: true, defaultSchedule: 'daily' },
      { key: 'campaignAbTest', label: 'A/B variant selection', desc: 'Auto-select A or B variant for each prospect', allowSchedule: false },
    ],
  },
  {
    key: 'prospects',
    title: 'Prospect Management',
    description: 'Lead scoring and data enrichment',
    actions: [
      { key: 'prospectScore', label: 'Auto-score prospects', desc: 'AI rates ICP fit based on company data and engagement signals', allowSchedule: true, defaultSchedule: 'weekly' },
      { key: 'prospectBrief', label: 'Refresh prospect briefs', desc: 'Re-generate briefs with latest company data', allowSchedule: true, defaultSchedule: 'weekly' },
      { key: 'prospectEnrich', label: 'Data enrichment', desc: 'Pull latest info from Apollo and update prospect records', allowSchedule: true, defaultSchedule: 'weekly' },
    ],
  },
  {
    key: 'alerts',
    title: 'Alerts & Reports',
    description: 'Notifications and recurring reports',
    actions: [
      { key: 'alertHotLead', label: 'Hot lead alert', desc: 'Instant notification when interested prospect replies', allowSchedule: false, modesOverride: ['auto', 'off'] },
      { key: 'alertDailyDigest', label: 'Daily digest', desc: 'Summary of all activity: sends, opens, replies, meetings', allowSchedule: true, defaultSchedule: 'daily', modesOverride: ['auto', 'off'] },
      { key: 'alertWeeklyReport', label: 'Weekly performance report', desc: 'Campaign metrics, reply rates, pipeline status', allowSchedule: true, defaultSchedule: 'weekly', modesOverride: ['auto', 'off'] },
      { key: 'alertBounceWarning', label: 'Bounce rate warning', desc: 'Alert when bounce rate exceeds threshold', allowSchedule: false, modesOverride: ['auto', 'off'] },
    ],
  },
];

const STATUS_MAP = {
  Success: { label: 'Verified', color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle },
  Pending: { label: 'Pending — check your inbox and click the confirmation link', color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: Clock },
  Failed: { label: 'Verification failed', color: 'bg-red-50 text-red-700 border-red-200', icon: AlertCircle },
  NotStarted: { label: 'Not verified', color: 'bg-slate-50 text-slate-500 border-slate-200', icon: AlertCircle },
  NotConfigured: { label: 'No email configured', color: 'bg-slate-50 text-slate-500 border-slate-200', icon: AlertCircle },
};

export default function Settings() {
  const [tab, setTab] = useState('email');
  const [form, setForm] = useState(() => {
    const auto = {};
    const approveOnly = ['replySendInterested', 'replySendObjection', 'campaignSend'];
    AUTOMATION_SECTIONS.forEach(s => s.actions.forEach(a => {
      auto[a.key] = { mode: approveOnly.includes(a.key) ? 'approve' : 'auto', schedule: a.defaultSchedule || 'none', customInterval: '' };
    }));
    return { senderEmail: '', senderName: '', bccEmail: '', notificationEmail: '', notificationPhone: '', calendlyUrl: '', automation: auto };
  });
  const [savedEmail, setSavedEmail] = useState('');
  const [verification, setVerification] = useState('NotConfigured');
  const [savingName, setSavingName] = useState(false);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState('');
  const [addingRecipient, setAddingRecipient] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    getEmailSettings()
      .then((data) => {
        const rawEmail = data.senderEmail || data.gmailAddress || '';
        const email = rawEmail.endsWith('@archer-outbound.com') ? rawEmail.replace('@archer-outbound.com', '') : rawEmail;
        const savedAuto = data.automation || {};
        const mergedAuto = {};
        AUTOMATION_SECTIONS.forEach(s => s.actions.forEach(a => {
          mergedAuto[a.key] = { mode: 'approve', schedule: a.defaultSchedule || 'none', customInterval: '', ...(savedAuto[a.key] || {}) };
        }));
        setForm({
          senderEmail: email,
          senderName: data.senderName || '',
          bccEmail: data.bccEmail || '',
          notificationEmail: data.notificationEmail || '',
          notificationPhone: data.notificationPhone || '',
          calendlyUrl: data.calendlyUrl || '',
          automation: mergedAuto,
        });
        setSavedEmail(email);
        setVerification(data.verificationStatus || 'NotConfigured');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    listRecipients()
      .then(res => setRecipients(res.recipients || []))
      .catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (verification !== 'Pending') {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const res = await getVerifyStatus();
        if (res.status && res.status !== 'Pending') {
          setVerification(res.status);
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (res.status === 'Success') {
            setMsg({ type: 'success', text: 'Email verified successfully! You can now send emails.' });
          }
        }
      } catch {}
    }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [verification]);

  const handleVerify = async () => {
    const trimmed = form.senderEmail.trim();
    if (!trimmed) { setMsg({ type: 'error', text: 'Email address is required' }); return; }
    if (!trimmed) { setMsg({ type: 'error', text: 'Username is required' }); return; }
    if (/[^a-zA-Z0-9._-]/.test(trimmed)) { setMsg({ type: 'error', text: 'Username can only contain letters, numbers, dots, hyphens, and underscores' }); return; }
    const fullEmail = trimmed + '@archer-outbound.com';
    const cleanForm = { ...form, senderEmail: fullEmail, senderName: form.senderName.trim() };
    setForm(cleanForm);
    setVerifying(true);
    setMsg(null);
    try {
      await saveEmailSettings(cleanForm);
      setSavedEmail(trimmed);
      const res = await verifyEmail();
      setVerification('Pending');
      setMsg({ type: 'success', text: res.message });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setVerifying(false);
    }
  };

  const handleSaveName = async () => {
    setSavingName(true);
    setMsg(null);
    try {
      const payload = { ...form, senderEmail: form.senderEmail + '@archer-outbound.com' };
      await saveEmailSettings(payload);
      setMsg({ type: 'success', text: 'Sender name updated!' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSavingName(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMsg(null);
    try {
      const res = await sendTestEmail(form.senderEmail + '@archer-outbound.com');
      setMsg({ type: 'success', text: `Test email sent to ${res.sentTo}` });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveGeneral = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const payload = { ...form, senderEmail: form.senderEmail + '@archer-outbound.com' };
      await saveEmailSettings(payload);
      setMsg({ type: 'success', text: 'Settings saved!' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleAddRecipient = async () => {
    const trimmedRecipient = newRecipient.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmedRecipient || !emailRegex.test(trimmedRecipient)) { setMsg({ type: 'error', text: 'Enter a valid email address' }); return; }
    setNewRecipient(trimmedRecipient);
    setAddingRecipient(true);
    setMsg(null);
    try {
      await addRecipient(trimmedRecipient);
      setRecipients(prev => [...prev, { email: trimmedRecipient, status: 'Pending' }]);
      setMsg({ type: 'success', text: `Verification sent to ${trimmedRecipient}` });
      setNewRecipient('');
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setAddingRecipient(false);
    }
  };

  const handleRemoveRecipient = async (email) => {
    try {
      await removeRecipient(email);
      setRecipients(prev => prev.filter(r => r.email !== email));
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  const refreshRecipients = async () => {
    try {
      const res = await listRecipients();
      setRecipients(res.recipients || []);
    } catch {}
  };

  const checkStatus = async () => {
    try {
      const res = await getVerifyStatus();
      setVerification(res.status || 'NotStarted');
      if (res.status === 'Success') setMsg({ type: 'success', text: 'Email verified!' });
      else if (res.status === 'Pending') setMsg({ type: 'info', text: 'Still pending. Check your inbox for the confirmation email.' });
    } catch {}
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const status = STATUS_MAP[verification] || STATUS_MAP.NotConfigured;
  const StatusIcon = status.icon;
  const isVerified = verification === 'Success';
  const isPending = verification === 'Pending';
  const emailChanged = savedEmail && form.senderEmail !== savedEmail;

  const MsgBanner = () => msg && (
    <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
      msg.type === 'success' ? 'bg-green-50 text-green-700' :
      msg.type === 'info' ? 'bg-blue-50 text-blue-700' :
      'bg-red-50 text-red-700'
    }`}>
      {msg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {msg.text}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your account preferences</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => { setTab(key); setMsg(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors flex-1 justify-center ${
              tab === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Email Sending Tab ── */}
      {tab === 'email' && (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Email Sending</h2>
                <p className="text-xs text-slate-500">Emails are sent via AWS SES. Just verify your email address — no passwords needed.</p>
              </div>
            </div>

            <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${status.color}`}>
              <StatusIcon className={`w-4 h-4 ${isPending ? 'animate-pulse' : ''}`} />
              <span className="flex-1">{status.label}</span>
              {isPending && (
                <button onClick={checkStatus} className="p-1 hover:bg-white/50 rounded" title="Refresh status">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Sender Email Address</label>
                <div className="flex items-center">
                  <input type="text" value={form.senderEmail} onChange={(e) => setForm({ ...form, senderEmail: e.target.value.replace(/[@\s]/g, '') })}
                    placeholder="username" className="flex-1 px-3 py-2 border border-slate-300 rounded-l-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <span className="px-3 py-2 bg-slate-100 border border-l-0 border-slate-300 rounded-r-lg text-sm text-slate-500 whitespace-nowrap">@archer-outbound.com</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">This is the "From" address recipients will see.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Sender Name</label>
                <input type="text" value={form.senderName} onChange={(e) => setForm({ ...form, senderName: e.target.value })}
                  placeholder="Your Name or Company" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">BCC Email (Copy)</label>
                <input type="email" value={form.bccEmail} onChange={(e) => setForm({ ...form, bccEmail: e.target.value })}
                  placeholder="your-gmail@gmail.com" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <p className="text-xs text-slate-400 mt-1">All outbound emails (campaigns + replies) will be BCC'd here. Great for keeping a copy in your Gmail.</p>
              </div>
            </div>

            <MsgBanner />

            <div className="flex flex-wrap gap-3 pt-2">
              {(!isVerified || emailChanged) && (
                <button onClick={handleVerify} disabled={verifying || !form.senderEmail}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                  {verifying && <Loader2 className="w-4 h-4 animate-spin" />}
                  {emailChanged ? 'Save & Verify New Email' : isPending ? 'Resend Verification' : 'Verify Email'}
                </button>
              )}
              {isVerified && !emailChanged && (
                <button onClick={handleSaveName} disabled={savingName}
                  className="px-4 py-2 bg-slate-600 text-white text-sm font-medium rounded-lg hover:bg-slate-700 disabled:opacity-50 flex items-center gap-2">
                  {savingName && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save Name
                </button>
              )}
              {isVerified && !emailChanged && (
                <button onClick={handleTest} disabled={testing}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
                  {testing && <Loader2 className="w-4 h-4 animate-spin" />}
                  Send Test Email
                </button>
              )}
            </div>
          </div>

          {/* Verified Recipients */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-slate-900">Verified Recipients</h2>
                <p className="text-xs text-slate-500 mt-0.5">During sandbox mode, you can only send to verified email addresses.</p>
              </div>
              <button onClick={refreshRecipients} className="p-1.5 text-slate-400 hover:text-slate-600 rounded" title="Refresh statuses">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            <div className="flex gap-2">
              <input type="email" value={newRecipient} onChange={(e) => setNewRecipient(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddRecipient()} placeholder="recipient@example.com"
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <button onClick={handleAddRecipient} disabled={addingRecipient || !newRecipient}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5">
                {addingRecipient ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add
              </button>
            </div>

            {recipients.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No recipients added yet.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {recipients.map(r => (
                  <div key={r.email} className="flex items-center justify-between py-2.5">
                    <div className="flex items-center gap-2.5">
                      {r.status === 'Success' ? <CheckCircle className="w-4 h-4 text-green-500" /> :
                       r.status === 'Pending' ? <Clock className="w-4 h-4 text-yellow-500" /> :
                       <AlertCircle className="w-4 h-4 text-slate-400" />}
                      <span className="text-sm text-slate-700">{r.email}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        r.status === 'Success' ? 'bg-green-50 text-green-600' :
                        r.status === 'Pending' ? 'bg-yellow-50 text-yellow-600' : 'bg-slate-50 text-slate-500'
                      }`}>
                        {r.status === 'Success' ? 'Verified' : r.status === 'Pending' ? 'Pending' : 'Not verified'}
                      </span>
                    </div>
                    <button onClick={() => handleRemoveRecipient(r.email)} className="p-1 text-slate-300 hover:text-red-500 rounded" title="Remove">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Notifications Tab ── */}
      {tab === 'notifications' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
              <Bell className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Notification Preferences</h2>
              <p className="text-xs text-slate-500">Get alerted when prospects reply. These are separate from your sending email.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notification Email</label>
              <input type="email" value={form.notificationEmail} onChange={(e) => setForm({ ...form, notificationEmail: e.target.value })}
                placeholder="boss@company.com" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <p className="text-xs text-slate-400 mt-1">Receive hot lead alerts and reply summaries here. Different from your sending address.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">SMS Phone Number</label>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-400" />
                <input type="tel" value={form.notificationPhone} onChange={(e) => setForm({ ...form, notificationPhone: e.target.value })}
                  placeholder="+1 555-123-4567" className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <p className="text-xs text-slate-400 mt-1">Get instant SMS when a hot lead replies. Leave blank to disable SMS alerts.</p>
            </div>
          </div>

          <MsgBanner />

          <button onClick={handleSaveGeneral} disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Notifications
          </button>
        </div>
      )}

      {/* ── Integrations Tab ── */}
      {tab === 'integrations' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <Link2 className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Integrations</h2>
              <p className="text-xs text-slate-500">Connect external tools to streamline your workflow.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Calendly URL</label>
              <input type="url" value={form.calendlyUrl} onChange={(e) => setForm({ ...form, calendlyUrl: e.target.value })}
                placeholder="https://calendly.com/your-name/30min" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <p className="text-xs text-slate-400 mt-1">When a prospect shows interest, this link will be included in the AI-drafted reply so they can book a meeting directly.</p>
            </div>
          </div>

          <MsgBanner />

          <button onClick={handleSaveGeneral} disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Integrations
          </button>
        </div>
      )}
      {/* ── Automation Tab ── */}
      {tab === 'automation' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Automation Controls</h2>
                <p className="text-xs text-slate-500">Set each action to Auto (fully automatic), Approve (needs your OK), Notify Only (just alert), or Off.</p>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 mt-4">
              {MODES.map(m => (
                <span key={m.value} className={`text-xs px-2.5 py-1 rounded-full border font-medium ${m.color}`}>{m.label}</span>
              ))}
            </div>
          </div>

          {AUTOMATION_SECTIONS.map(section => (
            <div key={section.key} className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-slate-900">{section.title}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{section.description}</p>
              </div>

              <div className="divide-y divide-slate-100">
                {section.actions.map(action => {
                  const val = form.automation[action.key] || { mode: 'approve', schedule: 'none' };
                  const availableModes = action.modesOverride
                    ? MODES.filter(m => action.modesOverride.includes(m.value))
                    : MODES;

                  return (
                    <div key={action.key} className="py-4 first:pt-0 last:pb-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">{action.label}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{action.desc}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* Mode selector */}
                          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                            {availableModes.map(m => (
                              <button key={m.value}
                                onClick={() => {
                                  const newAuto = { ...form.automation };
                                  newAuto[action.key] = { ...val, mode: m.value };
                                  setForm({ ...form, automation: newAuto });
                                }}
                                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                                  val.mode === m.value
                                    ? m.color
                                    : 'bg-white text-slate-400 hover:text-slate-600'
                                }`}>
                                {m.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Schedule selector */}
                      {action.allowSchedule && val.mode !== 'off' && (
                        <div className="mt-2 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          <select value={val.schedule || 'none'}
                            onChange={(e) => {
                              const newAuto = { ...form.automation };
                              newAuto[action.key] = { ...val, schedule: e.target.value };
                              setForm({ ...form, automation: newAuto });
                            }}
                            className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500">
                            {SCHEDULES.map(s => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                          {val.schedule === 'custom' && (
                            <input type="text" value={val.customInterval || ''}
                              onChange={(e) => {
                                const newAuto = { ...form.automation };
                                newAuto[action.key] = { ...val, customInterval: e.target.value };
                                setForm({ ...form, automation: newAuto });
                              }}
                              placeholder="e.g. every 6 hours"
                              className="text-xs border border-slate-200 rounded-md px-2 py-1 w-40 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <MsgBanner />

          <button onClick={handleSaveGeneral} disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Automation Settings
          </button>
        </div>
      )}
    </div>
  );
}
